/**
 * DemoBoardPage — zero-signup interactive demo, rebuilt 2026-07-11 as an
 * immersive single-screen board (founder direction: "make it so the user
 * wants to use it, play with it — like the actual board that gets built").
 *
 * Shape of the experience:
 *   1. Two quick questions — the same ones the real board wizard asks:
 *      child's age + how many words they use. Age picks the vocabulary
 *      band AND everything below it; words picks the grid density.
 *   2. A short "building your board" moment.
 *   3. The board, rendered INSIDE A MOCK TABLET (founder ruling: "still
 *      looks like a website — the way to break that is make a tablet and
 *      put it inside"). The board itself is BoardStage — the same live
 *      board embedded on the landing page (one product, one look).
 *
 * Still true, by design: no server calls, no account, nothing persists
 * when the tab closes. Speech is the browser's own Speech Synthesis.
 */
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RotateCcw, RotateCw, ChevronDown, Shield, Users, Sparkles, Palette } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { cancelSpeech } from "@/lib/tts";
import BoardStage from "@/components/demoboard/BoardStage";
import { AGE_BANDS, WORD_LEVELS, CATEGORIES, DEFAULT_COLORS } from "@/components/demoboard/vocabulary";
import "@/components/demoboard/demoboard.css";

// ---------------------------------------------------------------------------
// Setup — the two real-wizard questions (rendered inside the tablet)
// ---------------------------------------------------------------------------
function SetupScreen({ band, setBand, levelId, setLevelId, onBuild }) {
  return (
    <div className="tablet-setup" data-testid="demo-setup">
      <h1 className="display text-xl sm:text-2xl font-extrabold leading-tight mb-1">
        Let&apos;s build a board
      </h1>
      <p className="text-xs sm:text-sm text-[color:var(--ink-soft)] mb-4">
        Two quick questions — the same ones the real board wizard asks.
        Nothing you do here is saved.
      </p>

      <div className="mb-4">
        <div className="display font-extrabold text-sm mb-2">How old is your child?</div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Child's age">
          {AGE_BANDS.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => setBand(b.id)}
              className={`setup-chip ${band === b.id ? "is-selected" : ""}`}
              aria-pressed={band === b.id}
              data-testid={`demo-setup-age-${b.id}`}
            >
              {b.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-[color:var(--ink-soft)] mt-1.5">
          It brings in their age&apos;s words <strong>and the ones below it</strong> —
          every child starts where they are.
        </p>
      </div>

      <div className="mb-5">
        <div className="display font-extrabold text-sm mb-2">
          How many words do they use out loud today?
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Words used today">
          {WORD_LEVELS.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => setLevelId(l.id)}
              className={`setup-chip ${levelId === l.id ? "is-selected" : ""}`}
              aria-pressed={levelId === l.id}
              data-testid={`demo-setup-words-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-[color:var(--ink-soft)] mt-1.5">
          Fewer words → bigger tiles. More words → a fuller board. It grows
          with them.
        </p>
      </div>

      <button
        type="button"
        onClick={onBuild}
        className="pill-btn primary justify-center w-full text-base"
        data-testid="demo-setup-build"
      >
        Build the board <Sparkles size={16} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Building moment
// ---------------------------------------------------------------------------
const BUILD_COLORS = ["#FFB627", "#FF6B6B", "#4D96FF", "#6BCB77", "#C39BD3", "#7FB685"];
function BuildingScreen() {
  return (
    <div className="tablet-building" data-testid="demo-building">
      <div className="build-grid mb-6" aria-hidden>
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="build-cell"
            style={{ background: BUILD_COLORS[i % BUILD_COLORS.length], animationDelay: `${i * 90}ms` }}
          />
        ))}
      </div>
      <div className="display text-xl font-extrabold">Building the board…</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------
export default function DemoBoardPage() {
  useSEO({
    title: "Try SonSpeak — build a board in 10 seconds",
    description: "Answer two questions, get a real talking board. Tap tiles, hear speech, build a sentence. No signup.",
  });

  const [phase, setPhase] = useState("setup"); // setup | building | board
  const [band, setBand] = useState("C"); // sensible defaults so one tap works
  const [levelId, setLevelId] = useState(1);
  const [orient, setOrient] = useState("landscape"); // landscape | portrait — auto-follows the child on a real device
  // Colors are a PARENT setting (not on the child board). The demo exposes
  // them from the parents' bar above the tablet.
  const [colors, setColors] = useState(DEFAULT_COLORS);
  const [colorsOpen, setColorsOpen] = useState(false);
  // Remounts BoardStage per build so a rebuilt board starts fresh.
  const [buildCount, setBuildCount] = useState(0);
  const timers = useRef([]);

  const startBuild = () => {
    setPhase("building");
    setBuildCount((n) => n + 1);
    const t = setTimeout(() => setPhase("board"), 1400);
    timers.current.push(t);
  };

  const rebuild = () => {
    cancelSpeech();
    setPhase("setup");
  };

  return (
    <div data-testid="demo-board-page">
      <div className="demoboard-lock">
        {/* Slim top bar — the parents' corner, outside the "device" */}
        <header className="flex items-center gap-2 px-3 py-1.5 bg-[color:var(--cream)] border-b-[3px] border-[color:var(--ink)]">
          <Link to="/" className="pill-btn text-xs" data-testid="demo-back-home" aria-label="Back to home">
            <ArrowLeft size={12} />
          </Link>
          <div className="display font-extrabold text-sm sm:text-base leading-none">
            Buddy&apos;s board{" "}
            <span className="text-[9px] uppercase tracking-[0.2em] bg-[color:var(--coral)] text-white px-1.5 py-0.5 rounded align-middle">
              Demo
            </span>
          </div>
          <div className="text-[10px] text-[color:var(--ink)]/55 hidden md:block">
            Nothing you tap here is saved.
          </div>
          <div className="flex-1" />
          {phase === "board" && (
            <button
              type="button"
              onClick={() => setColorsOpen((v) => !v)}
              className={`pill-btn text-xs ${colorsOpen ? "primary" : ""}`}
              aria-pressed={colorsOpen}
              aria-expanded={colorsOpen}
              data-testid="demo-parent-colors"
            >
              <Palette size={12} /> <span className="hidden sm:inline">Colors</span>
            </button>
          )}
          {phase === "board" && (
            <button type="button" onClick={rebuild} className="pill-btn text-xs" data-testid="demo-rebuild">
              <RotateCcw size={12} /> Rebuild
            </button>
          )}
          <a href="#for-parents" className="pill-btn text-xs hidden sm:inline-flex" data-testid="demo-for-parents">
            For parents <ChevronDown size={12} />
          </a>
          <Link to="/register" className="pill-btn primary text-xs" data-testid="demo-sign-up">
            Sign up
          </Link>
        </header>

        {/* Parent color controls — a parent setting, above the tablet, never on
            the child's board. */}
        {phase === "board" && colorsOpen && (
          <div className="demo-parent-colors" data-testid="demo-parent-colors-panel">
            <span className="dpc-label">Board colors</span>
            {CATEGORIES.map((c) => (
              <label key={c.key} className="color-item">
                <input
                  type="color"
                  value={colors[c.key]}
                  onChange={(e) => setColors((s) => ({ ...s, [c.key]: e.target.value }))}
                  data-testid={`demo-color-${c.key}`}
                />
                <span>{c.label}</span>
              </label>
            ))}
            <label className="color-item">
              <input
                type="color"
                value={colors.bg}
                onChange={(e) => setColors((s) => ({ ...s, bg: e.target.value }))}
                data-testid="demo-color-bg"
              />
              <span>Background</span>
            </label>
            <button type="button" className="pill-btn text-xs" onClick={() => setColors(DEFAULT_COLORS)} data-testid="demo-color-reset">
              <RotateCcw size={12} /> Reset
            </button>
          </div>
        )}

        {/* The mock tablet — the child's device, drawn on the page. A Turn
            button shows the screen isn't orientation-locked.
            NOTE: the photo→living-tile picture generator normally sits beside
            the tablet in the board phase. Temporarily removed until the image
            provider + credits are wired up (restore
            <LivingTileMini idPrefix="demo-gen" /> in a .demo-generator-col
            below). Component kept at components/demoboard/LivingTileMini.jsx. */}
        <div className={`tablet-wrap ${phase === "board" ? "has-gen" : ""}`}>
          <div className="demo-tablet-col">
            <div
              className={`tablet ${phase === "board" ? `is-${orient}` : ""}`}
              data-testid="demo-tablet"
            >
              <span className="tablet-cam" aria-hidden />
              <div className="tablet-screen">
                {phase === "setup" && (
                  <SetupScreen
                    band={band}
                    setBand={setBand}
                    levelId={levelId}
                    setLevelId={setLevelId}
                    onBuild={startBuild}
                  />
                )}
                {phase === "building" && <BuildingScreen />}
                {phase === "board" && <BoardStage key={buildCount} band={band} levelId={levelId} colors={colors} idPrefix="demo" />}
              </div>
            </div>

            {phase === "board" && (
              <div className="demo-turn-row">
                <button
                  type="button"
                  className="pill-btn text-xs"
                  onClick={() => setOrient((o) => (o === "landscape" ? "portrait" : "landscape"))}
                  data-testid="demo-turn"
                >
                  <RotateCw size={14} /> Turn the tablet
                </button>
                <span className="demo-turn-note">
                  The screen follows your child — no flipping. A parent can lock the
                  orientation in Manage.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Below the board — the parents' story (scroll to reach; the board
          itself never scrolls) */}
      <section id="for-parents" className="bg-white border-t-[3px] border-[color:var(--ink)] px-4 py-8">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-3 gap-4">
          <div className="brutal-card p-4 bg-[color:var(--cream)]">
            <Shield size={18} className="mb-1" aria-hidden />
            <div className="display font-extrabold text-sm mb-1">Photos &amp; voice stay on device</div>
            <div className="text-xs text-[color:var(--ink)]/70 leading-snug">
              Every picture here is a sample. In the app, each tile is a real
              photo of YOUR family and your own recorded voice — which is
              exactly why they never touch any server.
            </div>
          </div>
          <div className="brutal-card p-4 bg-[color:var(--paper-muted)]">
            <Users size={18} className="mb-1" aria-hidden />
            <div className="display font-extrabold text-sm mb-1">Built around their age</div>
            <div className="text-xs text-[color:var(--ink)]/70 leading-snug">
              The two questions you just answered shape the whole board: their
              age brings in the right words plus the ones below, and the words
              they already use set how full each screen is.
            </div>
          </div>
          <div className="brutal-card p-4 bg-white">
            <Sparkles size={18} className="mb-1" aria-hidden />
            <div className="display font-extrabold text-sm mb-1">Tiles that never move</div>
            <div className="text-xs text-[color:var(--ink)]/70 leading-snug">
              Everything lives on one screen — no scrolling, no hunting. A word
              stays where it is every single day, so finding it becomes muscle
              memory.
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto mt-5 brutal-card p-4 bg-[color:var(--cream-deep)]" data-testid="demo-living-note">
          <div className="display font-extrabold text-sm mb-1 flex items-center gap-2">
            <span className="text-[9px] uppercase tracking-[0.08em] bg-[color:var(--coral)] text-white px-1.5 py-0.5 rounded">Live</span>
            Some tiles come alive
          </div>
          <div className="text-xs text-[color:var(--ink)]/75 leading-snug">
            Tap <strong>hello</strong>, <strong>Mom</strong>, <strong>music</strong>, or <strong>my pet</strong> —
            the picture moves for a second, then settles back so the word never
            gets lost. These sample clips are a preview; in the app they become
            your family&apos;s <strong>own photo and your own recorded voice</strong>,
            added by you. Want calm instead? Switch the board to <strong>Still</strong>,
            or <strong>Night</strong> for bedtime — the tiles never move, only the look changes.
          </div>
          {/* "Make one with your own photo → /living-studio" CTA temporarily
              removed with the picture generator; restore Monday when the image
              provider + credits are wired. */}
        </div>

        <div className="max-w-6xl mx-auto mt-4 text-xs font-semibold text-[color:var(--ink)]/75 leading-snug" data-testid="demo-safety-note">
          SonSpeak is in early access — it is not a medical, clinical, or
          emergency tool. For real-world safety or emergencies, contact your
          doctor or 911.{" "}
          <Link to="/demo-safety" className="underline">Learn more →</Link>
        </div>

        <div className="max-w-6xl mx-auto mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
          <Link to="/privacy-promise" className="underline" data-testid="demo-privacy-link">
            Read the full privacy promise →
          </Link>
          <Link to="/register" className="pill-btn primary" data-testid="demo-sign-up-footer">
            Sign up — start your child&apos;s real board
          </Link>
        </div>

        <div className="max-w-6xl mx-auto mt-6 text-[11px] text-[color:var(--ink-soft)] leading-snug">
          The board photos and the LIVE motion clips shown here are
          AI-generated placeholder imagery with intentionally blurred faces —
          a concept preview only, recorded in the demo attribution file. In the
          app, every tile is your family&apos;s own photo and your own recorded
          voice, added by you. This sample art is not production-cleared.
        </div>
      </section>
    </div>
  );
}
